import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ToastType } from "../../ToastManager/constants";
import { sendNotification } from "../../ToastManager/ToastManager";
import dynamic from "@redux-dynostore/react-redux";
import subspaced from "@redux-dynostore/react-redux-subspace";
import { attachReducer } from "@redux-dynostore/redux-subspace";
import { connect } from "react-redux";
import {
  mapDispatchToProps,
  mapStateToProps,
  caregiversTableReducer,
} from "./CaregiversTable.store";
import CustomModal from "../../ui-library/CustomModal/CustomModal";
import ConfirmRequest from "../../library/ConfirmRequest/ConfirmRequest";
import SmartTemporaryPassword from "../TemporaryPassword/TemporaryPassword";
import SmartAddCaregiver from "../AddCaregiver/AddCaregiver";
import { smoothScrollToTop } from "../../../modules/utils";
import { NotificationType } from "../../../modules/constants";
import DefaultTable, {
  ActionButtons,
  Line,
} from "../../ui-library/CustomTable/DefaultTable";
import { UserStatus } from "../../library/AccountStatus/AccountStatus.constants";
import PrimaryButton from "../../ui-library/Buttons/PrimaryButton/PrimaryButton";
import AccountStatus from "../../library/AccountStatus/AccountStatus";
import TableDropDown from "../../ui-library/TableDropDown/TableDropDown";
import { Dropdown } from "react-bootstrap";
import SmartEditCaregiver from "../../smart/EditCaregiver/EditCaregiver";

export const SmartCaregiversTable = (props) => {
  const {
    searchedParams,
    totalItems,
    caregivers,
    fetchCaregivers,
    unlockUser,
    blockUnblockUser,
    canCreateCaregiver,
    canViewCaregiverTemporaryPassword,
    canUnLockCaregiver,
    canUnblockCaregiver,
    resetState,
    loading,
    participant,
    canEditCaregiver,
  } = props;

  const [showTemporaryPasswordModal, setShowTemporaryPasswordModal] =
    useState(false);
  const [unlockingForTemporaryPassword, setUnlockingForTemporaryPassword] =
    useState(false);
  const [unblockingForTemporaryPassword, setUnblockingForTemporaryPassword] =
    useState(false);

  const [blockConfirmRequest, setBlockConfirmRequest] = useState(null);

  const [temporaryPasswordConfirmRequest, setTemporaryPasswordConfirmRequest] =
    useState(null);

  const [selectedCaregiver, setSelectedCaregiver] = useState(null);

  const [showAddCaregiverModal, setShowAddCaregiverModal] = useState(false);
  const [showEditCaregiverModal, setShowEditCaregiverModal] = useState(false);

  const getData = useCallback(
    (params) => {
      if (participant) {
        fetchCaregivers(participant.participantId, params)
          .catch((error) => {
            sendNotification(error, ToastType.ERROR);
          })
          .finally(() => {
            if (params.limit !== searchedParams.limit) {
              smoothScrollToTop();
            }
          });
      }
    },
    [participant, fetchCaregivers, searchedParams]
  );

  useEffect(() => {
    return () => resetState();
  }, [resetState]);

  const unlockAccount = (user) => {
    unlockUser(user.ugnxId)
      .then((message) => {
        sendNotification(message, ToastType.SUCCESS);
        if (canViewCaregiverTemporaryPassword) {
          generateTemporaryPassword(user, true, false);
        }

        getData(searchedParams);
      })
      .catch((error) => {
        sendNotification(error, ToastType.ERROR);
      });
  };

  const unblockAccount = (user) => {
    blockUnblockUser(user.ugnxId, false)
      .then((message) => {
        sendNotification(message, ToastType.SUCCESS);

        if (canViewCaregiverTemporaryPassword) {
          generateTemporaryPassword(user, false, true);
        }

        getData(searchedParams);
      })
      .catch((error) => {
        sendNotification(error, ToastType.ERROR);
      });
  };

  const blockAccount = (user) => {
    blockUnblockUser(user.ugnxId, true)
      .then((message) => {
        sendNotification(message, ToastType.SUCCESS);

        getData(searchedParams);
      })
      .catch((error) => {
        sendNotification(error, ToastType.ERROR);
      });
  };

  const closeTemporaryPasswordModal = () => {
    setShowTemporaryPasswordModal(false);
    setUnlockingForTemporaryPassword(false);
    setUnblockingForTemporaryPassword(false);
    setSelectedCaregiver(null);
  };

  const generateTemporaryPassword = (caregiver, isUnlocking, isUnblocking) => {
    setShowTemporaryPasswordModal(true);
    setUnlockingForTemporaryPassword(isUnlocking);
    setUnblockingForTemporaryPassword(isUnblocking);
    setSelectedCaregiver(caregiver);
  };

  const closeAddCaregiverModal = (caregiver) => {
    setShowAddCaregiverModal(false);

    if (caregiver) {
      if (canViewCaregiverTemporaryPassword) {
        generateTemporaryPassword(caregiver?.caregiver, false, false);
      }
    }

    getData(searchedParams);
  };

  const openEditCaregiverModal = (caregiver) => {
    setSelectedCaregiver(caregiver);
    setShowEditCaregiverModal(true);
  };

  const closeEditCaregiverModal = (refreshData = false) => {
    setShowEditCaregiverModal(false);
    setSelectedCaregiver(null);
    if (refreshData) {
      getData(searchedParams);
    }
  };

  const addCaregiver = () => {
    setShowAddCaregiverModal(true);
  };

  const closeConfirmRequestModal = () => {
    setTemporaryPasswordConfirmRequest(null);
    setBlockConfirmRequest(null);
  };

  const confirmRequestHandler = () => {
    if (temporaryPasswordConfirmRequest) {
      generateTemporaryPassword(temporaryPasswordConfirmRequest);
    } else if (blockConfirmRequest) {
      blockAccount(blockConfirmRequest);
    }

    closeConfirmRequestModal();
  };

  useEffect(() => {
    if (totalItems === null) {
      getData(searchedParams);
    }
  }, [getData, searchedParams, totalItems]);

  const confirmRequestMessage = useMemo(() => {
    if (temporaryPasswordConfirmRequest) {
      return "Clicking confirm will reset the user’s password. A new temporary password will be displayed on screen.";
    }

    if (blockConfirmRequest) {
      return "Clicking Confirm will block user from being able to login to Rarify. Unblocking a user will generate a new temporary password.";
    }

    return "";
  }, [temporaryPasswordConfirmRequest, blockConfirmRequest]);

  const getNotificationsInfo = (caregiverNotifications) => {
    if (!caregiverNotifications || caregiverNotifications.length === 0) {
      return "None";
    }

    if (
      caregiverNotifications.indexOf(NotificationType.EMAIL) !== -1 &&
      caregiverNotifications.indexOf(NotificationType.PHONE) !== -1
    ) {
      return "Email/SMS";
    }

    if (
      caregiverNotifications.indexOf(NotificationType.EMAIL) !== -1 &&
      caregiverNotifications.indexOf(NotificationType.PHONE) === -1
    ) {
      return "Email";
    }

    if (
      caregiverNotifications.indexOf(NotificationType.EMAIL) === -1 &&
      caregiverNotifications.indexOf(NotificationType.PHONE) !== -1
    ) {
      return "SMS";
    }
  };

  const getDropDownItems = (user) => {
    const dropDownMenuItems = {
      GENERATE_TEMP_PASSWORD: {
        text: "Generate Temporary Password",
        callBack: (caregiver) => setTemporaryPasswordConfirmRequest(caregiver),
        id: 2,
        isDisabled:
          user.statusId == UserStatus.LOCKED.id ||
          user.statusId == UserStatus.BLOCKED.id,
        isVisible: canViewCaregiverTemporaryPassword,
      },
      UNLOCK_ACCOUNT: {
        text: "Unlock Account",
        callBack: (caregiver) => unlockAccount(caregiver),
        id: 3,
        isDisabled: user.statusId !== UserStatus.LOCKED.id,
        isVisible: canUnLockCaregiver,
      },
      UNBLOCK_USER: {
        text: "Unblock User",
        callBack: (caregiver) => unblockAccount(caregiver),
        id: 8,
        isVisible:
          canUnblockCaregiver && user.statusId === UserStatus.BLOCKED.id,
      },
      BLOCK_USER: {
        text: "Block User",
        callBack: (caregiver) => setBlockConfirmRequest(caregiver),
        id: 9,
        isVisible:
          canUnblockCaregiver &&
          (user.statusId === UserStatus.ACTIVE.id ||
            user.statusId === UserStatus.INVITED.id),
      },
      EDIT_CAREGIVER: {
        id: 10,
        text: "Edit Caregiver",
        isVisible: canEditCaregiver,
        callBack: (caregiver) => openEditCaregiverModal(caregiver),
      },
    };

    return Object.values(dropDownMenuItems);
  };

  const defaultProps = {
    headers: [
      { label: "Site" },
      { label: "Participant ID" },
      { label: "Caregiver ID" },
      { label: "Relationship" },
      { label: "Notifications" },
      { label: "Time Zone" },
      { label: "Account Status" },
      { label: "Options" },
    ],
    hasSearch: false,
    haspagesizefilter: true,
    haspagination: true,
    searchedParams,
    totalitems: totalItems,
    loading,
    getData,
  };

  return (
    <>
      <CustomModal
        show={showAddCaregiverModal}
        title="Add Caregiver"
        onClose={closeAddCaregiverModal}
      >
        <SmartAddCaregiver
          onClose={closeAddCaregiverModal}
          caregivers={caregivers}
          participant={participant}
        />
      </CustomModal>

      <CustomModal
        show={showTemporaryPasswordModal && !!selectedCaregiver}
        title="Please use to login"
        onClose={closeTemporaryPasswordModal}
      >
        <SmartTemporaryPassword
          userId={selectedCaregiver?.userId}
          username={selectedCaregiver?.ugnxId}
          onClose={closeTemporaryPasswordModal}
          unlocking={unlockingForTemporaryPassword}
          unblocking={unblockingForTemporaryPassword}
        />
      </CustomModal>

      <CustomModal
        title="Are you sure?"
        onClose={closeConfirmRequestModal}
        show={!!temporaryPasswordConfirmRequest || !!blockConfirmRequest}
      >
        <ConfirmRequest
          message={confirmRequestMessage}
          onConfirm={confirmRequestHandler}
          onClose={closeConfirmRequestModal}
        />
      </CustomModal>

      <CustomModal
        show={showEditCaregiverModal}
        title="Edit Caregiver"
        onClose={closeEditCaregiverModal}
      >
        <SmartEditCaregiver
          onClose={() => closeEditCaregiverModal(true)}
          caregiver={selectedCaregiver}
        />
      </CustomModal>

      <DefaultTable {...defaultProps} data-testid="caregivers-table">
        <ActionButtons>
          {canCreateCaregiver && (
            <PrimaryButton
              data-testid="add-caregiver-btn"
              className="btn btn-accent btn-sm mr-2"
              onClick={addCaregiver}
            >
              Add Caregiver
            </PrimaryButton>
          )}
        </ActionButtons>
        {caregivers?.map((caregiver, index) => (
          <Line key={index}>
            <span>{caregiver.siteId}</span>
            <span>{caregiver.participantId}</span>
            <span>{caregiver.ugnxId}</span>
            <span>{caregiver.relationship}</span>
            <span>{getNotificationsInfo(caregiver.notifications)}</span>
            <span>{caregiver.timeZone}</span>
            <AccountStatus>{caregiver.statusId}</AccountStatus>
            <TableDropDown>
              {getDropDownItems(caregiver)
                .filter((dropDownItem) => dropDownItem.isVisible)
                .map((item, index) => (
                  <Dropdown.Item
                    key={index}
                    bsPrefix="dropdown-item"
                    disabled={item.isDisabled}
                    onClick={() => item.callBack(caregiver)}
                  >
                    {item.text}
                  </Dropdown.Item>
                ))}
            </TableDropDown>
          </Line>
        ))}
      </DefaultTable>
    </>
  );
};

export default dynamic(
  "caregiversTableReducer",
  subspaced({
    // We map extra state because we might need to acess other stores for data, subspace is self contained.
    mapExtraState: (state) => ({
      loginReducer: state.loginReducer,
    }),
  }),
  attachReducer(caregiversTableReducer)
)(connect(mapStateToProps, mapDispatchToProps)(SmartCaregiversTable));
